/**
 * OFC Game State Management
 *
 * Provides functions for:
 * - Creating initial game state
 * - Querying game state (actors, phase, available actions)
 * - Immutable state updates (setters return new state)
 * - End-of-hand results aggregation
 */
import { createEmptyBoard, getAvailablePositions, isBoardComplete, isBoardFouled } from './board';
import { ActingMode, OFCVariant, RowPosition } from './enums';
import { continuesFantasy, getFantasyCardCount, qualifiesForFantasy } from './fantasy';
import { Operation } from './operations';
import { calculateScores } from './scoring';
import { Board, Card, CreateOFCHandDto, GameState, Placement } from './types';
import { getCardsForRound, getCardsToDiscard, getCardsToPlace, getVariantConfig } from './variants';

/** Create initial game state for a new hand */
export function createInitialState(dto: CreateOFCHandDto): GameState {
  const boards = new Map<number, Board>();
  const playerIndexes: number[] = [];

  for (let i = 0; i < dto.playerCount; i++) {
    boards.set(i, createEmptyBoard(i));
    playerIndexes.push(i);
  }

  const actingMode = dto.actingMode ?? getDefaultActingMode(dto.variant);

  return {
    variant: dto.variant,
    status: true,
    roundIndex: null,
    playerCount: dto.playerCount,
    actingMode,
    actorIndex: actingMode === ActingMode.SEQUENTIAL ? 0 : null,
    actors: new Set(playerIndexes),
    boards,
    fantasyPlayers: new Set(dto.fantasyPlayers ?? []),
    discards: [],
    operations: [],
  };
}

/** Get default acting mode for a variant */
export function getDefaultActingMode(variant: OFCVariant): ActingMode {
  return getVariantConfig(variant).actingMode;
}

/** Get list of players who can currently act */
export function getActors(state: GameState): readonly number[] {
  if (!state.status) {
    return [];
  }

  if (state.actingMode === ActingMode.SEQUENTIAL) {
    return state.actorIndex !== null ? [state.actorIndex] : [];
  }

  return Array.from(state.actors);
}

/** Check if a specific player can act */
export function canAct(state: GameState, playerIndex: number): boolean {
  if (!state.status) {
    return false;
  }
  return state.actors.has(playerIndex);
}

/** Check if game is complete (status = false) */
export function isGameComplete(state: GameState): boolean {
  return !state.status;
}

/** Check if in initial deal phase (round 0) */
export function isInitialDeal(state: GameState): boolean {
  return state.status && state.roundIndex === 0;
}

/** Check if in placement phase (round > 0) */
export function isPlacementPhase(state: GameState): boolean {
  return state.status && state.roundIndex !== null && state.roundIndex > 0;
}

/** Check if all boards have 13 cards placed */
export function areAllBoardsComplete(state: GameState): boolean {
  for (const board of state.boards.values()) {
    if (!isBoardComplete(board)) {
      return false;
    }
  }
  return true;
}

/** Get next actor in sequential mode (wraps around) */
export function getNextActor(state: GameState): number | null {
  if (state.actingMode !== ActingMode.SEQUENTIAL) {
    return null;
  }

  const actorsArray = Array.from(state.actors);
  if (actorsArray.length === 0) {
    return null;
  }

  const currentIndex = state.actorIndex ?? -1;
  for (let i = currentIndex + 1; i < state.playerCount; i++) {
    if (state.actors.has(i)) {
      return i;
    }
  }

  for (let i = 0; i <= currentIndex; i++) {
    if (state.actors.has(i)) {
      return i;
    }
  }

  return null;
}

/** Check if a single placement is valid for the board */
export function isValidPlacement(board: Board, placement: Placement): boolean {
  return getAvailablePositions(board).includes(placement.position);
}

/** Check if multiple placements fit in available slots */
export function areValidPlacements(board: Board, placements: readonly Placement[]): boolean {
  const rowCounts = new Map<RowPosition, number>();

  for (const placement of placements) {
    const currentCount = rowCounts.get(placement.position) ?? 0;
    rowCounts.set(placement.position, currentCount + 1);
  }

  for (const [position, count] of rowCounts) {
    const row = board[position];
    const capacity = position === RowPosition.FRONT ? 3 : 5;
    if (count > capacity - row.cards.length) {
      return false;
    }
  }

  return true;
}

// ============================================================================
// Immutable State Updaters
// ============================================================================

/** Generic state update (spread operator) */
export function updateState(state: GameState, updates: Partial<GameState>): GameState {
  return { ...state, ...updates };
}

/** Add an operation to the operations log */
export function addOperation(state: GameState, operation: Operation): GameState {
  return {
    ...state,
    operations: [...state.operations, operation],
  };
}

/** Set game status (true = active, false = complete) */
export function setStatus(state: GameState, status: boolean): GameState {
  return { ...state, status };
}

export function setRoundIndex(state: GameState, roundIndex: number | null): GameState {
  return { ...state, roundIndex };
}

export function setBoard(state: GameState, playerIndex: number, board: Board): GameState {
  const newBoards = new Map(state.boards);
  newBoards.set(playerIndex, board);
  return { ...state, boards: newBoards };
}

export function setActors(state: GameState, actors: ReadonlySet<number>): GameState {
  return { ...state, actors };
}

export function removeActor(state: GameState, playerIndex: number): GameState {
  const newActors = new Set(state.actors);
  newActors.delete(playerIndex);
  return { ...state, actors: newActors };
}

export function setActorIndex(state: GameState, actorIndex: number | null): GameState {
  return { ...state, actorIndex };
}

export function addDiscard(state: GameState, card: Card): GameState {
  return { ...state, discards: [...state.discards, card] };
}

export function addDiscards(state: GameState, cards: readonly Card[]): GameState {
  return { ...state, discards: [...state.discards, ...cards] };
}

export function setFantasyPlayers(state: GameState, players: ReadonlySet<number>): GameState {
  return { ...state, fantasyPlayers: players };
}

export function addFantasyPlayer(state: GameState, playerIndex: number): GameState {
  const newFantasy = new Set(state.fantasyPlayers);
  newFantasy.add(playerIndex);
  return { ...state, fantasyPlayers: newFantasy };
}

// ============================================================================
// Card Usage Tracking
// ============================================================================

/** Get a player's board */
export function getBoard(state: GameState, playerIndex: number): Board | undefined {
  return state.boards.get(playerIndex);
}

/** Check if a card has been discarded */
export function isCardDiscarded(state: GameState, card: Card): boolean {
  return state.discards.some((d) => d.rank === card.rank && d.suit === card.suit);
}

/** Check if a card is on any player's board */
export function isCardOnAnyBoard(state: GameState, card: Card): boolean {
  for (const board of state.boards.values()) {
    const allCards = [...board.front.cards, ...board.middle.cards, ...board.back.cards];
    if (allCards.some((c) => c.rank === card.rank && c.suit === card.suit)) {
      return true;
    }
  }
  return false;
}

/** Check if a card is used (on board or discarded) */
export function isCardUsed(state: GameState, card: Card): boolean {
  return isCardDiscarded(state, card) || isCardOnAnyBoard(state, card);
}

// ============================================================================
// Game Flow Queries
// ============================================================================

/** Check if all players have acted this round */
export function isRoundComplete(state: GameState): boolean {
  return state.actors.size === 0;
}

/** Check if game should end (all boards complete or max rounds reached) */
export function shouldEndGame(state: GameState): boolean {
  if (!state.status) {
    return false;
  }
  if (areAllBoardsComplete(state)) {
    return true;
  }
  const config = getVariantConfig(state.variant);
  return state.roundIndex !== null && state.roundIndex >= config.totalRounds;
}

/** Get number of cards dealt this round */
export function getCardsDealtThisRound(state: GameState): number {
  if (state.roundIndex === null) {
    return 0;
  }
  const config = getVariantConfig(state.variant);
  return getCardsForRound(config, state.roundIndex);
}

/** Available actions for a player */
export interface AvailableActions {
  canPlace: boolean;
  requiredPlacements: number;
  requiredDiscards: number;
  availablePositions: RowPosition[];
}

/** Get available actions for a player (null if cannot act) */
export function getAvailableActions(state: GameState, playerIndex: number): AvailableActions | null {
  if (!state.status || !state.actors.has(playerIndex) || state.roundIndex === null) {
    return null;
  }

  const config = getVariantConfig(state.variant);
  const board = getBoard(state, playerIndex);
  if (!board) {
    return null;
  }

  // Fantasy players place all 13 cards at once
  // This happens either in round 0 (if all in fantasy) or after regular rounds complete
  const isFantasyPlayer = state.fantasyPlayers.has(playerIndex);
  if (isFantasyPlayer) {
    // Fantasy players always place 13 cards and discard 1 (from 14 dealt)
    return {
      canPlace: true,
      requiredPlacements: 13,
      requiredDiscards: 1,
      availablePositions: getAvailablePositions(board),
    };
  }

  return {
    canPlace: true,
    requiredPlacements: getCardsToPlace(config, state.roundIndex),
    requiredDiscards: getCardsToDiscard(config, state.roundIndex),
    availablePositions: getAvailablePositions(board),
  };
}

// ============================================================================
// Hand Results
// ============================================================================

/** Result for a single player at end of hand */
export interface PlayerResult {
  readonly playerIndex: number;
  readonly score: number;
  readonly fouled: boolean;
  readonly fantasy: {
    readonly wasInFantasy: boolean;
    readonly qualifiesForFantasy: boolean;
    readonly continuesFantasy: boolean;
    readonly nextHandCardCount: number;
  };
}

/** Complete hand results for all players */
export interface HandResults {
  readonly players: readonly PlayerResult[];
  readonly nextFantasyPlayers: readonly number[];
}

/** Get complete hand results (scores + fantasy info for next hand) */
export function getHandResults(state: GameState): HandResults {
  const scores = calculateScores(state);
  const players: PlayerResult[] = [];
  const nextFantasyPlayers: number[] = [];

  for (let i = 0; i < state.playerCount; i++) {
    const board = getBoard(state, i);
    const wasInFantasy = state.fantasyPlayers.has(i);
    const fouled = board ? isBoardFouled(board, state.variant) : false;

    let qualifies = false;
    let continues = false;
    let nextHandCardCount = 0;

    if (board && isBoardComplete(board) && !fouled) {
      if (wasInFantasy) {
        continues = continuesFantasy(board, state.variant);
        if (continues) {
          nextHandCardCount = getFantasyCardCount(board, state.variant);
          nextFantasyPlayers.push(i);
        }
      } else {
        qualifies = qualifiesForFantasy(board, state.variant);
        if (qualifies) {
          nextHandCardCount = getFantasyCardCount(board, state.variant);
          nextFantasyPlayers.push(i);
        }
      }
    }

    players.push({
      playerIndex: i,
      score: scores.get(i) ?? 0,
      fouled,
      fantasy: {
        wasInFantasy,
        qualifiesForFantasy: qualifies,
        continuesFantasy: continues,
        nextHandCardCount,
      },
    });
  }

  return { players, nextFantasyPlayers };
}
